import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { VENDORS, VENDOR_FILTERS } from "@/data/content";
import { Eyebrow } from "./Ornaments";
import { Reveal, scrollToId } from "./Reveal";

const VendorCard = ({ v }) => (
  <motion.article
    layout
    initial={{ opacity: 0, scale: 0.94 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.94 }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`vendor-card-${v.name.toLowerCase().replace(/\s+/g, "-")}`}
    className="group bg-white rounded-[26px] p-2.5 border border-terra/10 hover:shadow-[0_30px_60px_-30px_rgba(201,107,82,0.5)] transition-shadow duration-500"
  >
    <div className="relative aspect-[4/5] rounded-[20px] overflow-hidden">
      <img src={v.img} alt={v.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
      <span className="absolute bottom-3 left-3 rounded-full bg-cream/90 px-3 py-1 text-xs font-semibold text-ink">{v.category}</span>
    </div>
    <div className="px-2 pt-4 pb-2">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-xl text-ink">{v.name}</h3>
        <span className="flex items-center gap-1 text-xs text-muted-ink mt-1.5 shrink-0"><MapPin size={12} />{v.city}</span>
      </div>
      <p className="text-sm text-muted-ink mt-1">{v.note}</p>
    </div>
  </motion.article>
);

export const Vendors = () => {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? VENDORS : VENDORS.filter((v) => v.category === filter);
  return (
    <section id="vendors" data-testid="vendors-section" className="py-24 sm:py-32 bg-blush relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <Reveal>
            <Eyebrow>Vendor discovery</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink mt-5 leading-[1.05] max-w-2xl">
              Hand-picked people <span className="italic text-terra">behind the magic.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <button data-testid="vendors-join-cta" onClick={() => scrollToId("join")} className="btn-ghost">Are you a vendor? Join us <ArrowRight size={16} /></button>
          </Reveal>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 mb-10 -mx-5 px-5 sm:mx-0 sm:px-0 no-scrollbar" role="tablist">
          {VENDOR_FILTERS.map((f) => (
            <button
              key={f}
              role="tab"
              aria-selected={filter === f}
              data-testid={`vendor-filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`relative shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${filter === f ? "text-ink" : "text-muted-ink hover:text-ink bg-cream/60"}`}
            >
              {filter === f && <motion.span layoutId="vf" className="absolute inset-0 rounded-full bg-pink" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
              <span className="relative">{f}</span>
            </button>
          ))}
        </div>
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="vendor-grid">
          <AnimatePresence>
            {list.map((v) => <VendorCard key={v.name} v={v} />)}
          </AnimatePresence>
        </motion.div>
        <p className="text-xs text-muted-ink mt-8">Showcase listings shown for illustration — your verified vendor partners will appear here.</p>
      </div>
    </section>
  );
};
